import React from "react";
import { useTranslation } from "react-i18next";

import TextLink from "../../../TextLink";
import Tooltip from "../../../Tooltip";
import { ContactLink, ContactSector } from "../../../../content/contacts";

import style from "./styles.module.scss";

const keyI18n = "translations.contacts.card";

interface ContactCardProps {
  sector: ContactSector;
}

interface ContactRowProps {
  ariaLabel: string;
  children: React.ReactNode;
  icon: string;
}

function ContactRow({ ariaLabel, children, icon }: ContactRowProps) {
  return (
    <div className={style.row}>
      <span aria-hidden="true" className={`material-symbols-sharp ${style.icon}`}>
        {icon}
      </span>
      <span className={style.rowContent}>
        <span className={style.srOnly}>{ariaLabel}</span>
        {children}
      </span>
    </div>
  );
}

function Empty() {
  return (
    <span aria-hidden="true" className={`pgm-body-large ${style.empty}`}>
      —
    </span>
  );
}

function links(items: ContactLink[], external: boolean) {
  return items.map(({ href, label }, index) => (
    <React.Fragment key={href}>
      {index > 0 && <span className={`pgm-body-large ${style.separator}`}>/</span>}
      <TextLink
        className={`pgm-body-large ${style.link}`}
        external
        link={href}
        target={external ? "_blank" : undefined}
        text={label}
      />
    </React.Fragment>
  ));
}

export default function ContactCard({ sector }: ContactCardProps) {
  const { t } = useTranslation();
  const { emails, name, phones, whatsapps } = sector;

  return (
    <article className={style.card}>
      <Tooltip className={style.nameTooltip} text={name}>
        <h2 className={`pgm-title-medium ${style.name}`}>{name}</h2>
      </Tooltip>

      <ContactRow ariaLabel={t(`${keyI18n}.email`)} icon="mail">
        {emails.length > 0 ? links(emails, false) : <Empty />}
      </ContactRow>

      <ContactRow ariaLabel={t(`${keyI18n}.whatsapp`)} icon="chat">
        {whatsapps.length > 0 ? (
          <>
            {links(whatsapps, true)}
            <span className={`pgm-label-small ${style.tag}`}>
              {t(`${keyI18n}.whatsappTag`)}
            </span>
          </>
        ) : (
          <Empty />
        )}
      </ContactRow>

      <ContactRow ariaLabel={t(`${keyI18n}.phone`)} icon="phone">
        {phones.length > 0 ? (
          phones.map((phone, index) => (
            <React.Fragment key={phone}>
              {index > 0 && (
                <span className={`pgm-body-large ${style.separator}`}>/</span>
              )}
              <span className={`pgm-body-large ${style.phone}`}>{phone}</span>
            </React.Fragment>
          ))
        ) : (
          <Empty />
        )}
      </ContactRow>
    </article>
  );
}
